/**
 * ContraparteInput — "A quién" / "De quién" (spec aiChatBot 2026-10-17-registro-sobre-nota-
 * y-contraparte §2.3). Campo OPCIONAL de gastos, ingresos, stock y operaciones de hacienda.
 *
 * Autocompleta con las cuentas de la empresa, las personas del campo (personas_campo) y
 * los apodos de unas y otras ("Juancho" → Juan Gómez). Un nombre que no existe se puede
 * crear como persona del campo ("Crear «X»"); si el usuario escribe y no elige nada,
 * queda como nombre suelto (sin persona asociada). Vacío = sin contraparte. "Crear" sólo
 * aparece con permiso tools.create (el RLS de personas_campo): sin él, offline, el alta
 * quedaba en la cola y se rechazaba al sincronizar.
 *
 * Las cuentas no se sincronizan offline (company_users): se leen online y se guarda una
 * copia en localStorage para poder sugerirlas sin conexión.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getRepositories, getCurrentUserInfo } from '../../services/storage';
import { useAuthOptional } from '../../contexts/AuthContext';
import {
  buscarContrapartes, nombreCuenta, CuentaEmpresa, ContextoContraparte, OpcionContraparte,
} from '../../services/contraparte/contraparteSearch';

export interface ContraparteValor {
  user_id?: string | null;
  persona_id?: string | null;
  nombre: string;
}

interface Props {
  id: string;
  label: string;
  value: ContraparteValor | null;
  onChange: (v: ContraparteValor | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

const CACHE_KEY = (empresaId: string) => `contraparte_cuentas_${empresaId}`;

function leerCache(empresaId: string): CuentaEmpresa[] {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY(empresaId)) || '[]'); } catch { return []; }
}

async function cargarCuentas(empresaId: string): Promise<CuentaEmpresa[]> {
  try {
    const { permissionsService } = await import('../../services/permissionsService');
    const rows = await permissionsService.getCompanyUsers(empresaId);
    const cuentas = (rows || [])
      .filter((r: any) => !r.status || r.status === 'active')
      .map((r: any) => ({ user_id: r.user_id, nombre: r.nombre, apellido: r.apellido, email: r.email }));
    if (cuentas.length) {
      try { localStorage.setItem(CACHE_KEY(empresaId), JSON.stringify(cuentas)); } catch { /* sin storage */ }
      return cuentas;
    }
  } catch { /* sin conexión: copia local */ }
  return leerCache(empresaId);
}

export const ContraparteInput: React.FC<Props> = ({ id, label, value, onChange, placeholder, disabled }) => {
  const [texto, setTexto] = useState(value?.nombre || '');
  const [abierto, setAbierto] = useState(false);
  const [ctx, setCtx] = useState<ContextoContraparte>({ cuentas: [], personas: [], apodos: [] });
  const [empresaId, setEmpresaId] = useState<string | null>(null);
  const [creando, setCreando] = useState(false);
  const montado = useRef(true);
  const auth = useAuthOptional();
  const puedeCrearPersona = !!auth?.can('tools', 'create');

  useEffect(() => { setTexto(value?.nombre || ''); }, [value?.nombre]);

  useEffect(() => {
    montado.current = true;
    (async () => {
      try {
        const info = await getCurrentUserInfo();
        const emp = info?.empresaId;
        if (!emp || !montado.current) return;
        setEmpresaId(emp);
        const repos: any = getRepositories() || {};
        const [personas, apodos, cuentas] = await Promise.all([
          repos.personaCampo?.getByEmpresa ? repos.personaCampo.getByEmpresa(emp).catch(() => []) : [],
          repos.apodo?.getDePersonas ? repos.apodo.getDePersonas(emp).catch(() => []) : [],
          cargarCuentas(emp),
        ]);
        if (montado.current) setCtx({ personas: personas || [], apodos: apodos || [], cuentas: cuentas || [] });
      } catch { /* sin repos (tests/mobile): el campo funciona como texto */ }
    })();
    return () => { montado.current = false; };
  }, []);

  const opciones = useMemo(() => buscarContrapartes(texto, ctx), [texto, ctx]);
  const exacto = opciones.some((o) => o.nombre.toLowerCase() === texto.trim().toLowerCase());
  const puedeCrear = !!texto.trim() && !exacto && !!empresaId && puedeCrearPersona;

  const elegir = (o: OpcionContraparte) => {
    setTexto(o.nombre);
    setAbierto(false);
    onChange({ user_id: o.tipo === 'usuario' ? o.id : null, persona_id: o.tipo === 'persona' ? o.id : null, nombre: o.nombre });
  };

  const crear = async () => {
    const nombre = texto.trim();
    if (!nombre || !empresaId || !puedeCrearPersona) return;
    setCreando(true);
    try {
      const repos: any = getRepositories();
      const info = await getCurrentUserInfo().catch(() => null);
      const nuevoId = await repos.personaCampo.add({ nombre, empresa_id: empresaId, created_by: info?.userId || '' });
      setCtx((c) => ({ ...c, personas: [...c.personas, { id: nuevoId, empresa_id: empresaId, nombre, created_by: info?.userId || '' } as any] }));
      setAbierto(false);
      onChange({ user_id: null, persona_id: nuevoId, nombre });
    } finally {
      setCreando(false);
    }
  };

  return (
    <div className="relative">
      <label htmlFor={id} className="block text-sm font-medium text-ink mb-1">
        {label} <span className="text-sage font-normal">(opcional)</span>
      </label>
      <input
        id={id}
        type="text"
        autoComplete="off"
        disabled={disabled}
        aria-autocomplete="list"
        className="w-full rounded-md border border-hairline bg-white px-3 py-2 text-ink placeholder:text-sage/70 focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20"
        placeholder={placeholder || 'Persona, empresa o rol (ej: el veterinario)'}
        value={texto}
        onChange={(e) => {
          const v = e.target.value;
          setTexto(v);
          setAbierto(!!v.trim());
          onChange(v.trim() ? { user_id: null, persona_id: null, nombre: v.trim() } : null);
        }}
        onFocus={() => setAbierto(!!texto.trim())}
        onBlur={() => setTimeout(() => setAbierto(false), 150)}
      />
      {abierto && (opciones.length > 0 || puedeCrear) && (
        <ul id={`${id}-lista`} role="listbox" className="absolute z-20 mt-1 w-full max-h-60 overflow-auto rounded-md border border-hairline bg-white shadow-card">
          {opciones.map((o) => (
            <li
              key={`${o.tipo}-${o.id}`}
              role="option"
              aria-selected={false}
              className="cursor-pointer px-3 py-2 text-sm hover:bg-forest/10"
              onMouseDown={(e) => { e.preventDefault(); elegir(o); }}
            >
              <span className="text-ink">{o.nombre}</span>
              {o.via && <span className="text-sage"> · {o.via}</span>}
              <span className="ml-2 text-xs text-sage">{o.tipo === 'usuario' ? 'usuario de la app' : (o.rol || 'persona del campo')}</span>
            </li>
          ))}
          {puedeCrear && (
            <li
              role="option"
              aria-selected={false}
              className="cursor-pointer px-3 py-2 text-sm text-forest hover:bg-forest/10"
              onMouseDown={(e) => { e.preventDefault(); if (!creando) void crear(); }}
            >
              {`Crear «${texto.trim()}»`}
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

/** Valor del input → columnas contraparte_* (para los DTO de gasto/ingreso/stock/operación). */
export function camposDeContraparte(v: ContraparteValor | null) {
  return {
    contraparte_user_id: v?.user_id || null,
    contraparte_persona_id: v?.persona_id || null,
    contraparte_nombre: v?.nombre?.trim() || null,
  };
}

/** Columnas guardadas → valor del input (al editar). */
export function contraparteDeFila(f: { contraparte_user_id?: string | null; contraparte_persona_id?: string | null; contraparte_nombre?: string | null } | null | undefined): ContraparteValor | null {
  if (!f || !f.contraparte_nombre) return null;
  return { user_id: f.contraparte_user_id || null, persona_id: f.contraparte_persona_id || null, nombre: f.contraparte_nombre };
}

export { nombreCuenta };
export default ContraparteInput;
